/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React from "react";

import Link from "next/link";
import styled from "styled-components";

import { BaseUser, CurrentUser } from "src/classes/user";
import { Badges } from "src/components/Shared/User/Badges";
import { HoverCard } from "src/components/Shared/User/HoverCard";
import { ConditionalWrapper } from "src/components/UI/ConditionalWrapper/ConditionalWrapper";
import { UnstyledLink } from "src/components/UI/Links/Links";
import { isTouchDevice } from "src/lib/utils/device";

interface Props {
  user: BaseUser | CurrentUser;
  fontSize?: string;
  hoverable?: boolean;
  style?: React.CSSProperties;
  disableLink?: boolean;
}

export const Username = ({
  user,
  fontSize = "12px",
  hoverable = false,
  style = {},
  disableLink = false,
}: Props) => {
  return (
    <UsernameWrapper style={style}>
      {/* maybe wrap in HoverCard */}
      <ConditionalWrapper
        condition={hoverable && !isTouchDevice()}
        wrapper={(children: React.ReactNode) => <HoverCard user={user}>{children}</HoverCard>}
      >
        {/* maybe wrap in Link */}
        <ConditionalWrapper
          condition={!disableLink}
          wrapper={(children: React.ReactNode) => (
            <Link passHref href={user.inAppLink}>
              <UnstyledLink>{children}</UnstyledLink>
            </Link>
          )}
        >
          <StyledUsername style={{ fontSize, backgroundColor: user.color }}>
            {user.prefixedUsername}
          </StyledUsername>
        </ConditionalWrapper>
      </ConditionalWrapper>
      <Badges badges={user.snapshot.badges} />
    </UsernameWrapper>
  );
};

const UsernameWrapper = styled.div`
  margin-top: 4px;
  font-size: 12px; // required for proper alignment of text with badges
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
`;

const StyledUsername = styled.span`
  vertical-align: center;
  font-family: "IBM Plex Mono";
  font-size: 12px;
  color: ${({ theme }) => theme.text.default};
  :hover {
    text-decoration: underline;
  }
`;
